import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import {
  ATLASSIAN_PROFILE_URL,
  TRELLO_AA_HELP_URL,
  TWO_FACTOR_SETUP_URL,
} from '@trello/aa-migration';
import type { SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import { isTouch } from '@trello/browser';
import { forNamespace } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import type { PIIString } from '@trello/privacy';

import { ExternalLink } from './ExternalLink';
import { ListItem } from './ListItem';

// eslint-disable-next-line @trello/less-matches-component
import styles from './AtlassianAccountMigrationConfirmationDialog.less';

const format = forNamespace(['aa migration confirmation'], {
  shouldEscapeStrings: false,
});

interface AaInactiveMigrationContentProps {
  email: PIIString;
  onDismiss: () => void;
  analyticsSource: SourceType;
  analyticsContext: Parameters<typeof Analytics.sendUIEvent>[0]['attributes'];
}

export const AaInactiveMigrationContent: FunctionComponent<
  AaInactiveMigrationContentProps
> = ({ email, onDismiss, analyticsSource, analyticsContext }) => {
  const onClickGotItButton = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'dismissButton',
      source: analyticsSource,
      attributes: analyticsContext,
    });
    onDismiss();
  }, [analyticsContext, analyticsSource, onDismiss]);

  return (
    <div className={styles.description}>
      <ListItem>
        {format('email-login-message', {
          aaEmail: <strong key="email">{email}</strong>,
        })}
      </ListItem>
      <ListItem>
        {format('setup-2fa-message', {
          setup2faLink: (
            <ExternalLink
              href={TWO_FACTOR_SETUP_URL}
              key="setup-2fa-link"
              linkName={'setup2faLink'}
              analyticsContext={analyticsContext}
              analyticsSource={analyticsSource}
            >
              {format('setup-2fa-link')}
            </ExternalLink>
          ),
        })}
      </ListItem>
      <ListItem>
        {format('review-profile-message', {
          reviewProfileLink: (
            <ExternalLink
              href={ATLASSIAN_PROFILE_URL}
              key="review-profile-link"
              linkName={'manageProfileLink'}
              analyticsContext={analyticsContext}
              analyticsSource={analyticsSource}
            >
              {format('review-profile-link')}
            </ExternalLink>
          ),
        })}
      </ListItem>
      {isTouch() && <ListItem>{format('mobile-login-message')}</ListItem>}
      <div className={styles.buttonSection}>
        <Button appearance="primary" size="wide" onClick={onClickGotItButton}>
          {format('got it')}
        </Button>
        <ExternalLink
          href={TRELLO_AA_HELP_URL}
          linkName={'learnMoreLink'}
          analyticsContext={analyticsContext}
          analyticsSource={analyticsSource}
        >
          {format('learn more')}
        </ExternalLink>
      </div>
    </div>
  );
};
